import { Api } from 'domain/core';
import { StationData, Variable } from 'domain/models';
import { Resolution } from 'domain/models/api.models';
import useFetch from 'domain/services/auth/fetch.service';
import { triggerLinkClick } from 'domain/utils';
import { BaseQueryParams } from 'domain/utils/query.utils';
import { useEffect, useMemo, useState } from 'react';

export const autoResolution = (
	start?: string,
	end?: string
): Resolution | undefined => {
	if (!start || !end) {
		return 'HOUR';
	}

	// Get the difference in days between the start and end dates
	const diff =
		Math.abs(new Date(start).getTime() - new Date(end).getTime()) /
		(1000 * 60 * 60 * 24);

	if (diff >= 7) {
		return 'DAY';
	}
	if (diff >= 1) {
		return undefined;
	}
	return 'MONTH';
};

export const useObservationsExcelDownload = (
	stationId: number | string | undefined | (number | string)[],
	variables: string[],
	days?: number,
	resolution?: Resolution,
	start?: string,
	end?: string,
	filename?: string
) => {
	const authFetch = useFetch();

	const download = async (): Promise<any> => {
		if (!stationId || (Array.isArray(stationId) && stationId.length === 0)) {
			return Promise.reject();
		}

		if (!resolution) {
			resolution = autoResolution(start, end);
		}

		const variablesList = variables.join(',');

		const query = new BaseQueryParams(
			{
				key: 'stationsId',
				value: `${Array.isArray(stationId) ? stationId.join(',') : stationId}`,
			},
			{ key: 'variables', value: variablesList.toUpperCase() },
			{ key: 'days', value: days ? days.toString() : undefined },
			{ key: 'resolution', value: resolution },
			{ key: 'start', value: start },
			{ key: 'end', value: end },
			{
				key: 'timeZone',
				value: Intl.DateTimeFormat().resolvedOptions().timeZone,
			}
		);

		const url = `${Api.baseUrl}${Api.observations.seriesExcel}`;

		const response = await authFetch(
			`${url}${query ? '?' + query.getQueryParams() : ''}`
		);
		if (!response.ok) {
			throw new Error(response.statusText);
		}
		const blob = await response.blob();
		// Create a URL for the blob and download it
		const downloadURL = window.URL.createObjectURL(blob);
		triggerLinkClick(downloadURL, filename + '.xlsx');

		return Promise.resolve();
	};

	return download;
};

export const useObservations = (
	stationId: number | string | undefined | (number | string)[],
	variables: string[],
	days?: number,
	resolution?: Resolution,
	start?: string,
	end?: string,
	stationType: 'regular' | 'virtual' = 'regular'
): [StationData[], boolean] => {
	const [stationData, setStationData] = useState<StationData[]>([]);
	const [loading, setLoading] = useState<boolean>(false);

	const authFetch = useFetch();

	const load = async (): Promise<StationData[]> => {
		if (!stationId || (Array.isArray(stationId) && stationId.length === 0)) {
			return [];
		}

		if (!resolution) {
			resolution = autoResolution(start, end);
		}

		const variablesList = variables.join(',');

		const query = new BaseQueryParams(
			{
				key: 'stationsId',
				value: `${Array.isArray(stationId) ? stationId.join(',') : stationId}`,
			},
			{ key: 'variables', value: variablesList.toUpperCase() },
			{ key: 'days', value: days ? days.toString() : undefined },
			{ key: 'resolution', value: resolution },
			{ key: 'start', value: start },
			{ key: 'end', value: end }
		);

		const url =
			stationType === 'regular'
				? `${Api.baseUrl}${Api.observations.series}/`
				: `${Api.baseUrl}${Api.observations.virtualSeries}/${stationId}`;

		const response = await authFetch(
			`${url}${query ? '?' + query.getQueryParams() : ''}`
		);
		if (!response.ok) {
			throw new Error(response.statusText);
		}
		const data = await response.json();
		if (!data) {
			return [];
		}
		return data as StationData[];
	};

	const key = useMemo(() => {
		return `${stationId}-${variables.join(
			'-'
		)}-${days}-${resolution}-${start}-${end}`;
	}, [stationId, variables, days, resolution, start, end]);

	useEffect(() => {
		setLoading(true);
		load()
			.then((data) => {
				setStationData(data);
			})
			.catch((err) => {
				console.error(err);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [key]);

	return [stationData, loading];
};

export const useLatestObservations = (
	stationId: number,
	variables: Variable[]
): [StationData | undefined, boolean] => {
	const [stationData, setStationData] = useState<StationData>();
	const [loading, setLoading] = useState<boolean>(false);

	const authFetch = useFetch();

	const load = async (): Promise<StationData | undefined> => {
		const query = new BaseQueryParams({
			key: 'variables',
			value: variables.join(','),
		});

		const response = await authFetch(
			`${Api.baseUrl}${Api.observations.latest}/${stationId}${
				query ? '?' + query.getQueryParams() : ''
			}`
		);
		if (!response.ok) {
			throw new Error(response.statusText);
		}
		const data = await response.json();
		if (!data) {
			return undefined;
		}
		return data as StationData;
	};

	useEffect(() => {
		setLoading(true);
		load()
			.then((data) => {
				setStationData(data);
			})
			.catch((err) => {
				console.error(err);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [stationId]);

	return [stationData, loading];
};
